// A client in the client-documents tree: its retainers, then the jobs that have no
// retainer above them.
//
// A CLIENT HOLDS NO FILE OF ITS OWN, and that is the schema talking rather than a layout
// choice — every stored file names a retainer, a job, an invoice or a publication, and
// never a client directly. (The client's id is on the rows, but only so the object lands
// in the folder a client delete sweeps.) So this fold opens onto folds, with no table of
// its own at any point.
//
// A standalone job sits BELOW the retainers, one level shallower, because that is where
// it genuinely sits — the same call the client card's work tab makes, and for the same
// reason: wrapping one-offs in a fake "no retainer" group to even up the depth would be
// worse than the asymmetry it hid. It uses the same JobNode, at level 2 rather than 3.
import { useNavigate } from 'react-router-dom';
import he from '../../i18n/he';
import Collapsible from '../../components/Collapsible';
import Button from '../../components/Button';
import RetainerNode from './RetainerNode';
import JobNode from './JobNode';

export default function ClientNode({
  client, archived, open, onToggle, isRetainerOpen, onToggleRetainer, isJobOpen, onToggleJob,
}) {
  const navigate = useNavigate();

  return (
    <Collapsible
      level={1}
      open={open}
      onToggle={onToggle}
      spin
      title={client.name}
      badges={<span className="badge gray">{he.clientDocuments.documentCount(client.fileCount)}</span>}
      meta={
        <Button small variant="subtle" onClick={() => navigate(`/clients/${client.id}`)}>
          {he.clientDocuments.openClient}
        </Button>
      }
    >
      {client.retainers.length > 0 && (
        <>
          <h3>{he.clientDocuments.retainersHeading}</h3>
          {client.retainers.map((retainer) => (
            <RetainerNode
              key={retainer.id}
              retainer={retainer}
              archived={archived}
              open={isRetainerOpen(retainer.id)}
              onToggle={() => onToggleRetainer(retainer.id)}
              isJobOpen={isJobOpen}
              onToggleJob={onToggleJob}
            />
          ))}
        </>
      )}

      {client.jobs.length > 0 && (
        <>
          <h3>{he.clientDocuments.standaloneHeading}</h3>
          {client.jobs.map((job) => (
            <JobNode
              key={job.id}
              job={job}
              archived={archived}
              level={2}
              open={isJobOpen(job.id)}
              onToggle={() => onToggleJob(job.id)}
            />
          ))}
        </>
      )}
    </Collapsible>
  );
}
